import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Typography,
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  CircularProgress
} from '@mui/material'

import CampaignCardList from './CampaignCardList'
import DebouncedInput from '../../mailTemplate/src/components/atomic/atoms/DebouncedInput'

const filterAndSortCampaigns = (campaigns, searchTerm, statusFilter, sortBy) => {
  let filtered = campaigns

  if (statusFilter && statusFilter !== 'all') {
    filtered = filtered.filter(campaign => campaign.status === statusFilter)
  }

  if (searchTerm) {
    const search = String(searchTerm).toLowerCase()
    filtered = filtered.filter(campaign =>
      Object.values({
        currentCountryCode: campaign.currentCountryCode,
        importerCountryCode: campaign.importerCountryCode,
        hsCodes: campaign.hsCodes,
        name: campaign.name,
        productName: campaign.productName
      }).some(value =>
        String(value).toLowerCase().includes(search)
      )
    )
  }

  if (sortBy) {
    filtered = [...filtered].sort((a, b) => {
      const dateA = new Date(a.createdAt)
      const dateB = new Date(b.createdAt)

      if (sortBy === 'newest') {
        return dateB - dateA
      } else if (sortBy === 'oldest') {
        return dateA - dateB
      }
      return 0
    })
  }

  return filtered
}

const CAMPAIGN_STATUS_OPTIONS = [
  { value: 'all', label: 'mailTemplate.campaigns.allStatuses' },
  { value: 'SUBMITTED', label: 'mailTemplate.campaigns.status.SUBMITTED' },
  { value: 'PREPARING', label: 'mailTemplate.campaigns.status.PREPARING' },
  { value: 'READY_TO_SELECT', label: 'mailTemplate.campaigns.status.READY_TO_SELECT' },
  { value: 'READY_TO_MAIL', label: 'mailTemplate.campaigns.status.READY_TO_MAIL' },
  { value: 'MAILING', label: 'mailTemplate.campaigns.status.MAILING' },
  { value: 'FAILED', label: 'mailTemplate.campaigns.status.FAILED' }
]

const SORT_OPTIONS = [
  { value: 'newest', label: 'mailTemplate.campaigns.sort.newest' },
  { value: 'oldest', label: 'mailTemplate.campaigns.sort.oldest' }
]

const CampaignFilters = ({ searchTerm, onSearchChange, statusFilter, onStatusChange, sortBy, onSortChange, t, campaignCounts, onCreateCampaign }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'flex-end' }}>
    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'flex-end' }}>
      <DebouncedInput
        value={searchTerm || ''}
        onChange={onSearchChange}
        placeholder={t('mailTemplate.campaigns.search')}
        sx={{
          width: { xs: '100%', md: 300 },
          '& .MuiInputBase-root': {
            height: 40
          }
        }}
      />
      <FormControl sx={{ width: { xs: '100%', md: 250 } }}>
        <InputLabel>{t('mailTemplate.campaigns.filterByStatus')}</InputLabel>
        <Select
          value={statusFilter}
          onChange={(e) => onStatusChange(e.target.value)}
          label={t('mailTemplate.campaigns.filterByStatus')}
          size="small"
          sx={{
            height: 40,
            '& .MuiSelect-select': {
              paddingRight: '40px !important'
            }
          }}
        >
          {CAMPAIGN_STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 2 }}>
                <span>{t(option.label)}</span>
                {campaignCounts[option.value] !== undefined && (
                  <Chip
                    label={campaignCounts[option.value]}
                    size="small"
                    color={option.value === 'all' ? 'primary' : 'default'}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ width: { xs: '100%', md: 200 } }}>
        <InputLabel>{t('mailTemplate.campaigns.sortBy')}</InputLabel>
        <Select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          label={t('mailTemplate.campaigns.sortBy')}
          size="small"
          sx={{
            height: 40,
            '& .MuiSelect-select': {
              paddingRight: '40px !important'
            }
          }}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.label)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  </Box>
)

const CampaignList = ({
  campaigns,
  campaignReports,
  fetchCampaigns,
  onCreateCampaign,
  refreshCampaignReport,
  campaignReportsFetching,
  isLoading
}) => {
  const { t } = useTranslation()

  const [sortBy, setSortBy] = useState('newest')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const campaignCounts = useMemo(() => {
    const counts = campaigns.reduce((acc, campaign) => {
      acc[campaign.status] = (acc[campaign.status] || 0) + 1
      return acc
    }, {})

    counts.all = campaigns.length
    return counts
  }, [campaigns])

  const filteredCampaigns = useMemo(() =>
    filterAndSortCampaigns(campaigns, searchTerm, statusFilter, sortBy),
  [campaigns, searchTerm, statusFilter, sortBy]
  )

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            gap: 2,
            mb: 3
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              whiteSpace: 'nowrap'
            }}
          >
            {t('mailTemplate.campaigns.campaignsTitle')}
          </Typography>

          <CampaignFilters
            t={t}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            sortBy={sortBy}
            onSortChange={setSortBy}
            campaignCounts={campaignCounts}
            onCreateCampaign={onCreateCampaign}
          />
        </Box>
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400
        }}>
          <CircularProgress size={60}/>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2,
          mb: 3
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 700,
            color: 'primary.main',
            whiteSpace: 'nowrap'
          }}
        >
          {t('mailTemplate.campaigns.campaignsTitle')}
        </Typography>

        <CampaignFilters
          t={t}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          sortBy={sortBy}
          onSortChange={setSortBy}
          campaignCounts={campaignCounts}
          onCreateCampaign={onCreateCampaign}
        />
      </Box>
      <CampaignCardList
        data={filteredCampaigns}
        fetchCampaigns={fetchCampaigns}
        campaignReports={campaignReports}
        refreshCampaignReport={refreshCampaignReport}
        campaignReportsFetching={campaignReportsFetching}
        onCreateCampaign={onCreateCampaign}
      />
    </Box>
  )
}

export default CampaignList
